// THE ADDRESS THE RECORD LOOKUP IS MADE ON: THE OWNER'S UNIT FOLDED BACK IN (W2.4d).
//
// "The parcel we look up must be the parcel we file against" (SubjectPropertyView.swift:2527). In a condominium the
// UNIT is the parcel; looking up the bare street address finds the building. So `op:"record"` asks the provider about
// `SubjectProperty.lookupAddress` (Models.swift:435), and this file is its port.
//
// ── THE THREE THINGS IT HAS TO THREAD BETWEEN, EACH A LIVE FAILURE ──────────────────────────────────────────────────
//
// 1. **A BARE `contains` MATCHES A UNIT INSIDE AN ORDINARY STREET WORD.** Unit "F" on `1232 N Front St` read as
//    already-carried and returned the bare building address, silently dropping the unit — which files the appeal
//    against the building. So the token counts only where it FOLLOWS a designator word or a `#`, "which is the one
//    place a bare number in an address actually means a unit" (`streetCarriesUnit`, :516).
// 2. **MATCHING ONLY THE WHOLE NORMALIZED DESIGNATOR DOUBLES IT.** `1500 Locust St Apt 2210` with the field "2210"
//    became `…Apt 2210 Unit 2210`.
// 3. **A SPACE INSIDE THE LABEL TRUNCATES THE PROVIDER'S MATCH.** `functions/src/realie.ts`'s `UNIT_LABEL` captures a
//    contiguous run, so "Unit D 9" resolves to unit "D" — dropping the 9 and filing against a NEIGHBOUR's parcel with
//    no error anywhere in the chain (1590 Alta Dr, 2026-08-04). `collapsedUnitLabel` removes internal whitespace once,
//    here, so the backend regex never sees the space that would truncate it.
//
// ── THE WORD LIST IS A THIRD COPY, AND IT IS PINNED TO BOTH THE OTHERS ──────────────────────────────────────────────
//
// There are two already, deliberately: `UNIT_DESIGNATOR_WORDS` (functions/src/realie.ts:208) decides what the backend
// reads OUT of an address, and Swift's `unitDesignatorWords` (Models.swift:519) decides what goes IN. Swift's comment
// says what a divergence costs: "A word on one list only means we send '… Fl 10' and the backend sees no unit, so it
// looks up the BUILDING instead of the home." `UnitDesignatorTests` pins that pair.
//
// THIS FILE WOULD RATHER HAVE IMPORTED v1's, AND MEASURED THAT IT CANNOT (2026-09-16). `import { UNIT_DESIGNATOR_WORDS }
// from "../../realie"` typechecks, passes the inert-import audit, and DEADLOCKS the endpoint's own tests: `realie.ts`
// reaches `discrepancyLedger.ts`, which imports `firebase-admin/firestore` at its top level; the resolve tests mock that
// module with a factory that awaits `./world`; and `world.ts` imports this endpoint's handler. So loading the handler
// waits on a mock that waits on the handler. Every test file under `test/v2/resolve/` hung at import with no output —
// which is what made it worth writing down rather than quietly routing around: a v2 module that reaches v1 at its TOP
// LEVEL is the shape `deps.ts` already refuses for `firestore.ts`, and this is the same rule one module further out.
//
// So: a third copy, and `functions/test/v2/resolve/unitAddress.test.ts` reads BOTH of the others off disk — v1's export
// out of `src/realie.ts` and Swift's out of `Models.swift` — and fails on any difference, in either direction. Kept in
// sync by hand is what CLAUDE.md forbids; kept in sync by a test that parses both sources is what it asks for.
//
// LONGEST FIRST when the words are alternated, for the reason Swift's own comment gives: regex alternation is
// leftmost-match and not longest-match, so a plain list lets "fl" consume the front of "Floor 10" and leave "oor 10"
// as the unit. `realie.ts` sorts the same way for the same reason.
//
// Pure: no clock, no I/O.

import { trimmed } from "../valuation/format";

/** `UNIT_DESIGNATOR_WORDS` (functions/src/realie.ts:208) and `SubjectProperty.unitDesignatorWords` (Models.swift:519),
 *  in their order, character for character. Pinned to both — see the header. */
export const UNIT_DESIGNATOR_WORDS: readonly string[] = [
  "unit", "apt", "apartment", "suite", "ste", "room", "rm",
  "floor", "fl", "lot", "bldg", "building", "space", "spc", "trlr",
];

/** The words, longest first, as an alternation.
 *
 *  BUILT IN A FUNCTION BODY, not bound to a top-level `const`, because a `const` initializer in functions/src/v2/ may
 *  only be built from literals and builder calls (docs/W2_DESIGN.md 6.4, and functions/test/v2/importIsInert.test.ts
 *  enforces it — it caught this one). Three calls a lookup is not a cost worth a cached module-level value. */
function designatorAlternation(): string {
  return [...UNIT_DESIGNATOR_WORDS].sort((a, b) => b.length - a.length).join("|");
}

/** `collapsedUnitLabel` (Models.swift:510): internal whitespace removed, once. */
function collapsedUnitLabel(s: string): string {
  return s.split(" ").join("");
}

/** `splitDesignator` (Models.swift:526): "Apt. 2210" → ("Apt", "2210"), tolerating a trailing period, any casing and a
 *  missing space. Null when the text does not begin with a designator. The word is re-emitted CAPITALIZED and without
 *  its period, so a filed address reads cleanly and the backend's parser sees the spelling it expects. */
export function splitDesignator(s: string): { word: string; rest: string } | null {
  const m = new RegExp(`^(?:${designatorAlternation()})\\.?\\s*`, "i").exec(s);
  if (m === null) return null;
  const matched = m[0];
  const word = matched.replace(/[ .]+$/, "").replace(/^[ .]+/, "");
  const capitalized = word.length === 0 ? word : word[0].toUpperCase() + word.slice(1).toLowerCase();
  const rest = trimmed(s.slice(matched.length).replace(/^#\s*/, ""));
  return { word: capitalized, rest };
}

/** `normalizedUnitDesignator` (Models.swift:477, static since R115): the owner's unit entry, normalized to one canonical designator, or
 *  null when blank.
 *
 *  A `#` alone is not preserved — "it is punctuation, not a word, and providers parse the spelled form more reliably".
 *  A designator the owner typed themselves is KEPT however they punctuated it: an exact word match turned "Apt. 704"
 *  into "Unit Apt. 704" (2026-07-30, Tyler: typing the designator must never break the lookup). And "Unit" with nothing
 *  after it is not a unit — it is an abandoned edit, and appending it would send "200 Lombard St Unit" to a provider. */
export function normalizedUnitDesignator(unitNumber: string | null): string | null {
  const raw = trimmed(unitNumber ?? "").replace(/\s+/g, " ");
  const stripped = trimmed(raw.replace(/^#\s*/, ""));
  if (stripped.length === 0) return null;
  const split = splitDesignator(stripped);
  if (split === null) return `Unit ${collapsedUnitLabel(stripped)}`;
  if (split.rest.length === 0) return null;
  return `${split.word} ${collapsedUnitLabel(split.rest)}`;
}

/** `bareUnitToken` (Models.swift:542, static since R115): the unit with its designator and `#` stripped ("Apt 2210" → "2210"). */
export function bareUnitToken(unitNumber: string | null): string | null {
  const d = normalizedUnitDesignator(unitNumber);
  if (d === null) return null;
  const split = splitDesignator(d);
  if (split === null) return trimmed(d).length === 0 ? null : d;
  return split.rest.length === 0 ? null : split.rest;
}

/** A function declaration and not an arrow `const`, for the reason `designatorAlternation` gives. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** `streetCarriesUnit` (Models.swift:552): does the street text ALREADY name this unit — the token appearing after a
 *  designator word or a `#`, in any of the spellings a person or provider might use? */
export function streetCarriesUnit(street: string, unitNumber: string | null): boolean {
  const token = bareUnitToken(unitNumber);
  if (token === null || token.length === 0) return false;
  const pattern = new RegExp(`(?:\\b(?:${UNIT_DESIGNATOR_WORDS.join("|")})\\b\\.?|#)\\s*#?\\s*${escapeRegExp(token)}\\b`, "i");
  return pattern.test(street);
}

/** `SubjectProperty.lookupAddress` (Models.swift:435). The address, with the unit inserted after the street segment. */
export function lookupAddress(address: string, unitNumber: string | null): string {
  const designator = normalizedUnitDesignator(unitNumber);
  if (designator === null) return address;
  const comma = address.indexOf(",");
  const street = comma < 0 ? trimmed(address) : trimmed(address.slice(0, comma));
  if (streetCarriesUnit(street, unitNumber)) return address;
  const rest = comma < 0 ? "" : address.slice(comma);
  return `${street} ${designator}${rest}`;
}
